import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// region Imports - External libraries
import { FormHandles } from "@unform/core";
import * as Yup from "yup";
// endregion Imports - External libraries
// region Imports - Languages
import { ManagementMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared / Utils
import api from "@services/api";
import { User } from "@shared/interfaces/user.interface";
import { Hardware } from "@shared/interfaces/hardware.interface";
import { AlertTypesID } from "@shared/constants/alert-types.enum";
import { Vehicle } from "@shared/entities/reg_vehicles.entity";
import { Status } from "@shared/types/status.enum";

import { TpeAlertWithFunctionality, UserFunctionalityID } from "../types";
import { getUserValidationSchema } from "../schemas";
import { getSecurityAlerts } from "../constants";

// endregion Imports - Shared / Utils

interface UseUserFormProps {
  t: (key: string) => string;
  addToast: (props: any) => void;
  readUsers: () => void;
  table: DataTables.Api;
  tableClickedRow: JQuery<HTMLTableRowElement>;
  activeOptionsPlants: any[]; // Plant[]
  setActiveOptionsPlants: (plants: any[]) => void;
}
export function useUserForm({
  t,
  addToast,
  readUsers,
  table,
  tableClickedRow,
  activeOptionsPlants,
  setActiveOptionsPlants
}: UseUserFormProps) {
  const formRef = useRef<FormHandles>(null);

  // region States
  const [userDetails, setUserDetails] = useState<User>({} as User);
  const [modalType, setModalType] = useState<"details" | "update" | "register">("details");
  const [openModalFormUser, setOpenModalFormUser] = useState(false);
  const [openDialogConfirmSendValidationEmail, setOpenDialogConfirmSendValidationEmail] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [loadingUserList, setLoadingUserList] = useState(false);
  const [loadingResendConfirmAccountMail, setLoadingResendConfirmAccountMail] = useState(false);
  const [loadingValidateFieldEmail, setLoadingValidateFieldEmail] = useState(false);
  const [fetchedVehicles, setFetchedVehicles] = useState<Vehicle[]>([]);
  const [validateFields, setValidateFields] = useState<{ email: boolean }>({ email: false });
  const [activeCheckbox, setActiveCheckbox] = useState<boolean>(true);
  const [dailyReportCheckbox, setDailyReportCheckbox] = useState<boolean>(false);
  const [weeklyReportCheckbox, setWeeklyReportCheckbox] = useState<boolean>(false);
  const [dailyCanReportCheckbox, setDailyCanReportCheckbox] = useState<boolean>(false);
  const [weeklyCanReportCheckbox, setWeeklyCanReportCheckbox] = useState<boolean>(false);
  const [adminCheckbox, setAdminCheckbox] = useState<boolean>(false);
  const [optionsPermissions, setOptionsPermissions] = useState<TpeAlertWithFunctionality[]>([]);
  const [openOptionsPermissions, setOpenOptionsPermissions] = useState(false);
  const [activeOptionsPermissions, setActiveOptionsPermissions] = useState<TpeAlertWithFunctionality[]>([]);
  const [checkAllPermissions, setCheckAllPermissions] = useState(false);
  const [optionsRestrictVehicles, setOptionsRestrictVehicles] = useState<Vehicle[]>([]);
  const [activeRestrictVehicles, setActiveRestrictVehicles] = useState<Vehicle[] | undefined>(undefined);
  const [activeRestrictVehicleIds, setActiveRestrictVehicleIds] = useState<string[]>([]);
  // endregion States
  // region Constants
  const loadingOptionsPermissions = openOptionsPermissions && optionsPermissions.length === 0;

  const ALERTS = useMemo(() => getSecurityAlerts(t), [t]);
  const sortedOptionsPermissions = useMemo(() => [...optionsPermissions].sort((a, b) =>
    Number(!!b.isAFunctionality) - Number(!!a.isAFunctionality)
    || (a.description as string).localeCompare(b.description as string)
  ), [optionsPermissions]);
  // endregion Constants
  // region Functions

  /**
   * Resets the form states to their default values.
   */
  const resetFormStates = useCallback(() => {
    setActiveCheckbox(true);
    setActiveOptionsPlants([]);
    setActiveOptionsPermissions([]);
    setAdminCheckbox(false);
    setDailyReportCheckbox(false);
    setWeeklyReportCheckbox(false);
    setDailyCanReportCheckbox(false);
    setWeeklyCanReportCheckbox(false);
    setUserDetails({} as User);
    setValidateFields({ email: false });
    setActiveRestrictVehicles(undefined);
    setActiveRestrictVehicleIds([]);
    setOptionsRestrictVehicles([]);
    setOptionsPermissions([]);
  }, [setActiveOptionsPlants]);

  /**
   * A callback function to handle the closing of the modal form.
   * This function resets the form states and closes the modal form.
   */
  const handleCloseModalForm = useCallback(() => {
    setOpenModalFormUser(false);
    setTimeout(() => {
      resetFormStates();
    }, 0);
  }, [resetFormStates]);

  /**
   * Callback function to handle the creation of a user.
   * This function sets the modal type to "register" and opens the user form modal.
   */
  const handleUserCreate = useCallback(() => {
    resetFormStates();
    setModalType("register");
    setOpenModalFormUser(true);
  }, [resetFormStates]);

  /**
   * Retrieves detailed user information based on the specified user ID.
   *
   * The function fetches data from the API endpoint `users/read/{userId}` with additional query parameters:
   * - `linkedPlants`: Indicates whether to include linked plants in the response.
   * - `lastSentAlert`: Indicates whether to include the last sent alert in the response.
   * - `restrictedVehicles`: Indicates whether to include restricted vehicles in the response.
   *
   * @param {string} userId - The ID of the user for whom the details are to be retrieved.
   */
  const getUserDetails = useCallback(async (userId: string) => {
    try {
      const { data } = await api.get(`users/read/${userId}`, {
        params: {
          linkedPlants: true,
          lastSentAlert: true,
          restrictedVehicles: true
        }
      });

      return Array.isArray(data.result) ? data.result[0] : data.result;
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

      return null;
    }
  }, [addToast, t]);

  /**
   * Handles retrieving and processing user details, including permissions, settings, and various configurations.
   *
   * @param {User} user The user object containing the ID required to fetch details.
   */
  const handleUserDetails = useCallback(async (user: User) => {
    setLoadingUserDetails(true);
    const details = await getUserDetails(user.id_user);

    if (!details) {
      setLoadingUserDetails(false);

      return;
    }

    const {
      ALLOWED_CHANGE_PARAMS, ALLOWED_CHANGE_VEHICLES, ALLOWED_CHANGE_DRIVERS,
      ALLOWED_CHANGE_LOCATIONS, ALLOWED_BLOCK_VEHICLE
    } = ALERTS;

    const permissions = [...details.alerts_permissions];

    /* eslint-disable max-len */
    if (details.allowed_change_params && !permissions.some((a: any) => a.idFunctionality === UserFunctionalityID.ALLOWED_CHANGE_PARAMS)) permissions.push(ALLOWED_CHANGE_PARAMS);
    if (details.allowed_change_vehicles && !permissions.some((a: any) => a.idFunctionality === UserFunctionalityID.ALLOWED_CHANGE_VEHICLES)) permissions.push(ALLOWED_CHANGE_VEHICLES);
    if (details.allowed_change_drivers && !permissions.some((a: any) => a.idFunctionality === UserFunctionalityID.ALLOWED_CHANGE_DRIVERS)) permissions.push(ALLOWED_CHANGE_DRIVERS);
    if (details.allowed_change_locations && !permissions.some((a: any) => a.idFunctionality === UserFunctionalityID.ALLOWED_CHANGE_LOCATIONS)) permissions.push(ALLOWED_CHANGE_LOCATIONS);
    if (details.allowed_vehicle_block && !(details.alerts_permissions || []).some((a: any) => a.idFunctionality === UserFunctionalityID.ALLOWED_BLOCK_VEHICLE)) permissions.push(ALLOWED_BLOCK_VEHICLE);
    /* eslint-enable max-len */

    setUserDetails(details);
    setActiveOptionsPlants(details.plants);
    setActiveCheckbox(details.active);
    setAdminCheckbox(details.admin);
    setValidateFields({ email: true });
    setDailyReportCheckbox(details.receive_daily_report);
    setWeeklyReportCheckbox(details.receive_weekly_report);
    setDailyCanReportCheckbox(details.receive_daily_can_report);
    setWeeklyCanReportCheckbox(details.receive_weekly_can_report);
    setActiveOptionsPermissions(permissions);
    setActiveRestrictVehicles(details.restricted_vehicles);
    setActiveRestrictVehicleIds(details.restricted_vehicles.map((v: any) => v.id_vehicle!));
    setModalType("details");
    setOpenModalFormUser(true);
    setLoadingUserDetails(false);
  }, [getUserDetails, ALERTS, setActiveOptionsPlants]);

  /**
   * Validates an email address against unique field constraints.
   *
   * @param {string} email - The email address to be validated.
   */
  const validateEmail = async (email: string) => {
    setValidateFields({ ...validateFields, email: false });
    if (email.trim().length >= 3 && email.trim() !== userDetails.email) {
      try {
        setLoadingValidateFieldEmail(true);
        const { data } = await api.get(`users/verify-unique-field/email/${email.trim()}`);

        if (data.status === "alert") formRef.current?.setFieldError("email", t(ManagementMessages.formEmailRegistered));
        else formRef.current?.setFieldError("email", "");
        setValidateFields({ ...validateFields, email: true });
      } catch (error) {
        if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
      } finally {
        setLoadingValidateFieldEmail(false);
      }
    }
  };

  /**
   * Creates a new user and submits the data to the server.
   *
   * @param {User} user - The user object containing the data for the new user.
   * @param {boolean} sendMail - Indicates whether to send redefinition mail to the user.
   */
  const createUser = useCallback(async (user: User, sendMail: boolean) => {
    // eslint-disable-next-line no-param-reassign
    delete user.passwordConfirm;

    try {
      let insertUser = { ...user, sendMail };

      if (activeOptionsPlants.length) insertUser.plants = activeOptionsPlants;
      if (activeOptionsPermissions.length) {
        insertUser.alerts_permissions = activeOptionsPermissions;
        const {
          ALLOWED_CHANGE_PARAMS, ALLOWED_CHANGE_VEHICLES, ALLOWED_CHANGE_DRIVERS,
          ALLOWED_CHANGE_LOCATIONS
        } = ALERTS;

        insertUser.allowed_vehicle_block = insertUser.alerts_permissions.some((p: any) => p.id_alert_type === AlertTypesID.BLOQUEIO);
        insertUser.allowed_change_params = insertUser.alerts_permissions.some((p: any) => p.description === ALLOWED_CHANGE_PARAMS.description);
        insertUser.allowed_change_vehicles = insertUser.alerts_permissions.some((p: any) => p.description === ALLOWED_CHANGE_VEHICLES.description);
        insertUser.allowed_change_drivers = insertUser.alerts_permissions.some((p: any) => p.description === ALLOWED_CHANGE_DRIVERS.description);
        insertUser.allowed_change_locations = insertUser.alerts_permissions.some((p: any) => p.description === ALLOWED_CHANGE_LOCATIONS.description);
      }

      if (activeRestrictVehicleIds.length > 0) {
        insertUser.restricted_vehicles = activeRestrictVehicleIds.map((id) => ({ id_vehicle: id })) as Vehicle[];
      } else insertUser.restricted_vehicles = [];

      console.log("Insert User: ", insertUser);
      Object.keys(insertUser).forEach((key) => {
        const value = insertUser[key];

        if (
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === "string" && value.length === 0)
        ) {
          delete insertUser[key];
        }
      });

      setLoadingUserList(true);
      const { data } = await api.post("users/create", insertUser);

      if (data.status === "success") {
        handleCloseModalForm();
        setOpenDialogConfirmSendValidationEmail(false);
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });

        readUsers(); // Refresh list

        if (table?.row) {
          table.row.add(data.result);
          table.draw();
          table.search(user.email).draw();
        }
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingUserList(false);
    }
  }, [activeOptionsPlants, activeOptionsPermissions, activeRestrictVehicleIds, ALERTS, handleCloseModalForm, addToast, t, readUsers, table]);

  /**
   * Updates the details of a user with the provided changes and makes an API request to persist the updates.
   * The function is wrapped with `useCallback` to memoize it, preventing unnecessary re-creation during renders.
   *
   * @param {User} user The user object containing the changes to be updated.
   */
  const updateUser = useCallback(async (user: User) => {
    // eslint-disable-next-line no-param-reassign
    delete user.passwordConfirm;

    try {
      const uUser = { ...user };

      if (activeOptionsPlants.length) uUser.plants = activeOptionsPlants;

      if (activeOptionsPermissions.length) {
        // Filter out functionalities, keeping only real alerts for the relationship
        uUser.alerts_permissions = activeOptionsPermissions.filter(p => !p.isAFunctionality);

        const {
          ALLOWED_CHANGE_PARAMS, ALLOWED_CHANGE_VEHICLES, ALLOWED_CHANGE_DRIVERS,
          ALLOWED_CHANGE_LOCATIONS
        } = ALERTS;

        // Use the original full list 'activeOptionsPermissions' to check for functionalities
        uUser.allowed_vehicle_block = activeOptionsPermissions.some((p: any) => p.id_alert_type === AlertTypesID.BLOQUEIO);
        uUser.allowed_change_params = activeOptionsPermissions.some((p: any) => p.description === ALLOWED_CHANGE_PARAMS.description);
        uUser.allowed_change_vehicles = activeOptionsPermissions.some((p: any) => p.description === ALLOWED_CHANGE_VEHICLES.description);
        uUser.allowed_change_drivers = activeOptionsPermissions.some((p: any) => p.description === ALLOWED_CHANGE_DRIVERS.description);
        uUser.allowed_change_locations = activeOptionsPermissions.some((p: any) => p.description === ALLOWED_CHANGE_LOCATIONS.description);
      } else {
        uUser.alerts_permissions = [];
        uUser.allowed_vehicle_block = false;
        uUser.allowed_change_params = false;
        uUser.allowed_change_vehicles = false;
        uUser.allowed_change_drivers = false;
        uUser.allowed_change_locations = false;
      }

      if (activeRestrictVehicleIds.length > 0) {
        uUser.restricted_vehicles = activeRestrictVehicleIds.map((id) => ({ id_vehicle: id })) as Vehicle[];
      } else uUser.restricted_vehicles = [];

      Object.keys(uUser).forEach((key) => {
        const value = uUser[key];

        if (
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === "string" && value.length === 0)
        ) {
          if (key !== "telegram_id" && key !== "restricted_vehicles") {
            delete uUser[key];
          }
        }
      });

      setLoadingUserList(true);
      const { data } = await api.patch(`users/update/${userDetails.id_user}`, uUser);

      if (data.status === "success") {
        handleCloseModalForm();
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });
        setUserDetails(Object.assign(userDetails, uUser));

        if (table?.row) {
          table.row(tableClickedRow).data(userDetails);
          table.draw();
        }
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingUserList(false);
    }
  }, [userDetails, activeOptionsPlants, activeOptionsPermissions, activeRestrictVehicleIds, ALERTS, handleCloseModalForm, addToast, t, table,
    tableClickedRow]);

  /**
   * Asynchronously validates form data based on the provided action.
   *
   * @param {User} formData - The user data object to validate, containing fields like email, password, etc.
   * @param {"details" | "register" | "update"} action - The action for which the form is being validated.
   *    - "details": Validate user details.
   *    - "register": Validate form for user registration. May involve sending a validation email.
   *    - "update": Validate form for updating user information.
   *
   * @throws {Yup.ValidationError} If the form data does not pass schema validation, validation errors
   * will be captured, processed, and set in the form's error state.
   */
  const validateForm = async (formData: User, action: "details" | "register" | "update") => {
    try {
      if (!validateFields.email && formData.email.trim().length) await validateEmail(formData.email);
      const schema = getUserValidationSchema(t, activeOptionsPlants.length, formRef.current?.getFieldError("email"));

      await schema.validate(formData, { abortEarly: false });

      if (action === "register") {
        formData.password.length > 0 ? await createUser(formData, false) : setOpenDialogConfirmSendValidationEmail(true);
      } else {
        await updateUser(formData);
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: any = {};

        err.inner.forEach((error) => {
          validationErrors[error.path!] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      }
    }
  };

  /**
   * Asynchronous callback function to resend the confirmation email for account activation.
   */
  const resendConfirmAccountMail = useCallback(async () => {
    try {
      setLoadingResendConfirmAccountMail(true);
      const { data } = await api.post("auth/send-confirm-account-mail", { email: userDetails.email });

      if (data.status === "success") addToast({ type: "success", title: t(ToastMessages.success), description: data.message });
      else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingResendConfirmAccountMail(false);
    }
  }, [addToast, userDetails.email, t]);

  /**
   * A callback function to dynamically determine and return appropriate helper text
   * based on the user's sent alerts history and their status.
   */
  const getHelperTextToTelegram = useCallback(() => {
    return (userDetails.sent_alerts_history
      && userDetails.sent_alerts_history?.length > 0
      && userDetails.sent_alerts_history[0].status === Status.ERROR)
      ? t(ManagementMessages.detailsTelegramIdInfo)
      : t(ManagementMessages.detailsTelegramError);
  }, [userDetails, t]);

  /**
   * A callback function that handles the "Check All Permissions" checkbox state change.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event triggered by the checkbox interaction.
   */
  const handleCheckAllPermissionsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckAllPermissions(event.target.checked);
    if (event.target.checked) {
      setActiveOptionsPermissions(sortedOptionsPermissions);
    } else {
      setActiveOptionsPermissions([]);
    }
  }, [sortedOptionsPermissions]);

  /**
   * Determines the value to be returned based on the current modal type.
   *
   * @param {any} detailValue - The value to return if the modal type is "details".
   * @param {any} updateValue - The value to return if the modal type is "update".
   * @param {any} registerValue - The value to return if the modal type is "register".
   */
  const defineValueAccordingModalType = useCallback((detailValue, updateValue, registerValue) => {
    switch (modalType) {
    case "details":
      return detailValue;
    case "update":
      return updateValue;
    case "register":
      return registerValue;
    default:
      return "S/N";
    }
  }, [modalType]);
  // endregion Functions
  // region Effects

  // Restricted Vehicles - Fetch API
  useEffect(() => {
    if (activeOptionsPlants?.length <= 0) {
      setFetchedVehicles([]);

      return;
    }

    let isActive = true;

    (async () => {
      try {
        const customPlantsFilter = activeOptionsPlants.map((plant) => plant.id_plant);
        const { data } = await api.get("vehicles/read", { params: { linkedHardware: true, customPlantsFilter } });

        if (isActive && data.status === "success") {
          setFetchedVehicles(data.result as Vehicle[]);
        } else if (isActive) {
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        }
      } catch (error) {
        if (!isActive) return;
        if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
      }
    })();

    return () => { isActive = false; };
  }, [activeOptionsPlants, addToast, t]);

  // Restricted Vehicles - Merge with Selected
  useEffect(() => {
    if (!activeRestrictVehicles) {
      if (modalType === "register") setActiveRestrictVehicles([]);

      return;
    }

    const result = [...fetchedVehicles]; // Clone to avoid mutation if fetchedVehicles is reused

    if (activeRestrictVehicles.length > 0) {
      for (const vehicle of activeRestrictVehicles) {
        if (!result.some((vehicleResult) => vehicleResult.id_vehicle === vehicle.id_vehicle)) {
          result.push(vehicle);
        }
      }
    }

    result.sort((a, b) => (a.code || "").localeCompare(b.code || ""));
    setOptionsRestrictVehicles(result);
  }, [fetchedVehicles, activeRestrictVehicles, modalType]);

  // Fetch permissions options
  useEffect(() => {
    if (!openOptionsPermissions) {
      setOptionsPermissions([]);

      return;
    }
    let active = true;

    if (!loadingOptionsPermissions) return;

    (async () => {
      try {
        const { data } = await api.get("alerts/types/read");
        const hardwareData = await api.get("hardwares/read");

        if (data.status === "success" && hardwareData.data.status === "success") {
          if (active) {
            let options: TpeAlertWithFunctionality[];

            if (hardwareData.data.result.some((hardware: Hardware) => hardware.block_capable)) {
              options = data.result.filter(
                (type: TpeAlertWithFunctionality) => type.id_alert_type !== AlertTypesID.INATIVIDADE_DE_HARDWARE
                  && type.id_alert_type !== AlertTypesID.MANUTENCAO
              );
              const blockIndex = options.findIndex((type: TpeAlertWithFunctionality) => type.id_alert_type === AlertTypesID.BLOQUEIO);

              if (blockIndex !== -1) {
                options[blockIndex].isAFunctionality = true;
                options[blockIndex].idFunctionality = UserFunctionalityID.ALLOWED_BLOCK_VEHICLE;
              }
            } else {
              options = data.result.filter(
                (type: TpeAlertWithFunctionality) => type.id_alert_type !== AlertTypesID.BLOQUEIO
                  && type.id_alert_type !== AlertTypesID.INATIVIDADE_DE_HARDWARE
                  && type.id_alert_type !== AlertTypesID.MANUTENCAO
              );
            }
            const { ALLOWED_CHANGE_PARAMS, ALLOWED_CHANGE_VEHICLES, ALLOWED_CHANGE_DRIVERS, ALLOWED_CHANGE_LOCATIONS } = ALERTS;

            options.push(ALLOWED_CHANGE_PARAMS, ALLOWED_CHANGE_VEHICLES, ALLOWED_CHANGE_DRIVERS, ALLOWED_CHANGE_LOCATIONS);
            setOptionsPermissions(options);
          }
        } else {
          setOpenOptionsPermissions(false);
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        }
      } catch (error) {
        setOpenOptionsPermissions(false);
        if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
      }
    })();

    return () => { active = false; };
  }, [openOptionsPermissions, loadingOptionsPermissions, addToast, t, ALERTS]);
  // endregion Effects

  return {
    formRef,
    userDetails,
    modalType,
    setModalType,
    openModalFormUser,
    setOpenModalFormUser,
    openDialogConfirmSendValidationEmail,
    setOpenDialogConfirmSendValidationEmail,
    loadingUserDetails,
    loadingUserList,
    loadingResendConfirmAccountMail,
    loadingValidateFieldEmail,
    handleUserCreate,
    handleUserDetails,
    handleCloseModalForm,
    activeCheckbox, setActiveCheckbox,
    adminCheckbox, setAdminCheckbox,
    dailyReportCheckbox, setDailyReportCheckbox,
    weeklyReportCheckbox, setWeeklyReportCheckbox,
    dailyCanReportCheckbox, setDailyCanReportCheckbox,
    weeklyCanReportCheckbox, setWeeklyCanReportCheckbox,
    activeOptionsPermissions, setActiveOptionsPermissions,
    activeRestrictVehicles, setActiveRestrictVehicles,
    activeRestrictVehicleIds, setActiveRestrictVehicleIds,
    optionsRestrictVehicles, setOptionsRestrictVehicles,
    optionsPermissions, setOptionsPermissions,
    openOptionsPermissions, setOpenOptionsPermissions,
    loadingOptionsPermissions,
    checkAllPermissions,
    setCheckAllPermissions, // Exposed explicitly
    handleCheckAllPermissionsChange,
    validateForm,
    createUser,
    updateUser,
    resendConfirmAccountMail,
    getHelperTextToTelegram,
    defineValueAccordingModalType,
    validateEmail
  };
}
